import { site, brand } from "@/content";

const providerName = site.brandName;
const domain = (site as { domain?: string }).domain?.replace(/\/$/, "") ?? "";

/** Schema.org requires absolute URLs; relative paths are silently ignored by some parsers. */
function abs(path: string) {
  return path.startsWith("http") ? path : `${domain}${path}`;
}

export function breadcrumbSchema(items: { name: string; item: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.item),
    })),
  };
}

export function serviceSchema(input: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: input.name,
    description: input.description,
    url: abs(input.url),
    serviceType: input.serviceType ?? input.name,
    provider: organizationRef(),
    areaServed: { "@type": "Country", name: "Australia" },
  };
}

function organizationRef() {
  return {
    "@type": "AccountingService",
    "@id": `${domain}/#organization`,
    name: providerName,
    url: `${domain}/`,
  };
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AccountingService",
    "@id": `${domain}/#organization`,
    name: providerName,
    url: `${domain}/`,
    logo: abs("/images/icon-mark.png"),
    image: abs("/jamee-full.webp"),
    description:
      "CPA led strategic bookkeeping, management reporting, payroll, accounts management and Xero support for established Australian businesses.",
    areaServed: { "@type": "Country", name: "Australia" },
    founder: personSchemaInner(),
    knowsAbout: [
      "Bookkeeping",
      "BAS and IAS preparation",
      "Management reporting",
      "Payroll and Single Touch Payroll",
      "Accounts payable and receivable",
      "Xero setup and migration",
    ],
    slogan: brand.positioning,
  };
}

function personSchemaInner() {
  return {
    "@type": "Person",
    "@id": `${domain}/#jamee-white`,
    name: "Jamee White",
    honorificSuffix: "CPA",
    jobTitle: "Founder and Strategic Lead",
    image: abs("/images/jamee.webp"),
    worksFor: { "@id": `${domain}/#organization` },
    url: `${domain}/about`,
    award: [
      "LUCA Bookkeeping Awards 2024 finalist",
      "Women in Finance Awards 2024 finalist",
      "Women in Finance Awards 2025 finalist",
      "Women in Accounting Awards 2024 finalist",
    ],
  };
}

export function personSchema() {
  return {
    "@context": "https://schema.org",
    ...personSchemaInner(),
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

export function articleSchema(input: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  keywords?: string[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.title,
    description: input.description,
    mainEntityOfPage: { "@type": "WebPage", "@id": abs(input.url) },
    url: abs(input.url),
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    inLanguage: "en-AU",
    keywords: input.keywords?.join(", "),
    author: personSchemaInner(),
    publisher: {
      "@type": "Organization",
      "@id": `${domain}/#organization`,
      name: providerName,
      logo: { "@type": "ImageObject", url: abs("/images/icon-mark.png") },
    },
  };
}
